import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Megaphone, Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  title: "", short_text: "", long_text: "",
  center: "ambos", start_date: "", end_date: "",
  active: true, position: "drawer", variant: "informativo",
  button1_text: "", button1_url: "", button2_text: "", button2_url: "",
  show_on_mobile: true, dismissible: true, order: 100,
};

const POSITION_LABEL = {
  top_bar: "Barra superior",
  drawer: "Cajón bajo menú",
  home_slide: "Slide home",
  booking_form: "Formulario de cita",
};
const VARIANT_LABEL = { informativo: "Informativo", importante: "Importante", urgente: "Urgente" };
const CENTER_LABEL = { mostoles: "Móstoles", madrid: "Madrid", ambos: "Ambos" };

// Convierte el error de axios/FastAPI en un string entendible
const parseApiError = (e, fallback = "Error") => {
  const detail = e?.response?.data?.detail;
  if (!detail) return e?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
        const msg = d?.msg || "Error";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(" | ");
  }
  return JSON.stringify(detail);
};

// Construye el payload listo para el backend (Pydantic-friendly)
const buildPayload = (form) => ({
  title: (form.title || "").trim(),
  short_text: (form.short_text || "").trim(),
  long_text: (form.long_text || "").trim(),
  center: form.center || "ambos",
  start_date: (form.start_date || "").trim(),
  end_date: (form.end_date || "").trim(),
  active: !!form.active,
  position: form.position || "drawer",
  variant: form.variant || "informativo",
  button1_text: (form.button1_text || "").trim(),
  button1_url: (form.button1_url || "").trim(),
  button2_text: (form.button2_text || "").trim(),
  button2_url: (form.button2_url || "").trim(),
  show_on_mobile: !!form.show_on_mobile,
  dismissible: !!form.dismissible,
  order: Number.isFinite(parseInt(form.order, 10)) ? parseInt(form.order, 10) : 100,
});

export default function WebAnnouncementsPage() {
  const { axiosAuth } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const load = async () => {
    try {
      const r = await axiosAuth().get("/admin/web-announcements");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(parseApiError(e, "Error cargando avisos"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY);
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...EMPTY, ...a });
    setDialogOpen(true);
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const payload = buildPayload(form);
    // Validación local mínima
    if (!payload.title) return toast.error("Falta el título");
    if (!payload.short_text) return toast.error("Falta el texto corto");
    if (payload.start_date && payload.end_date && payload.start_date > payload.end_date) {
      return toast.error("La fecha de inicio no puede ser posterior a la de fin");
    }

    setSaving(true);
    try {
      if (editing) {
        await axiosAuth().put(`/admin/web-announcements/${editing.id}`, payload);
        toast.success("Aviso actualizado");
      } else {
        await axiosAuth().post("/admin/web-announcements", payload);
        toast.success("Aviso creado");
      }
      closeDialog();
      await load();
    } catch (e) {
      toast.error(parseApiError(e, "Error al guardar"));
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (a) => {
    setDeleteTarget(a);
    setDeleteConfirm("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirm.trim().toUpperCase() !== "ELIMINAR") {
      return toast.error('Escribe "ELIMINAR" en mayúsculas para confirmar');
    }
    try {
      await axiosAuth().delete(`/admin/web-announcements/${deleteTarget.id}`);
      toast.success("Aviso eliminado");
      setDeleteTarget(null);
      setDeleteConfirm("");
      await load();
    } catch (e) {
      toast.error(parseApiError(e, "Error al eliminar"));
    }
  };

  return (
    <div className="space-y-6" data-testid="web-announcements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light flex items-center gap-2 text-slate-900">
            <Megaphone className="w-5 h-5" /> Avisos web
          </h1>
          <p className="text-xs text-slate-500 mt-1">Mensajes en la parte pública. Edítalos sin tocar código.</p>
        </div>
        <Button onClick={openNew} className="rounded-sm" data-testid="ann-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo aviso
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm uppercase tracking-wider">Avisos existentes</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-slate-500">Cargando...</p>
            : items.length === 0 ? <p className="text-sm text-slate-500">Sin avisos. Crea el primero.</p>
            : (
              <div className="space-y-2">
                {items.map(a => (
                  <div key={a.id} className="border rounded-sm p-3 flex items-start justify-between gap-3 hover:bg-slate-50" data-testid={`ann-row-${a.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <strong className="text-sm">{a.title}</strong>
                        <Badge variant={a.active ? "default" : "outline"} className="text-[10px]">{a.active ? "Activo" : "Inactivo"}</Badge>
                        <Badge variant="outline" className="text-[10px]">{POSITION_LABEL[a.position] || a.position}</Badge>
                        <Badge variant="outline" className="text-[10px]">{VARIANT_LABEL[a.variant] || a.variant}</Badge>
                        <Badge variant="outline" className="text-[10px]">{CENTER_LABEL[a.center] || a.center}</Badge>
                        {(a.start_date || a.end_date) && (
                          <span className="text-[10px] text-slate-500 font-mono">{a.start_date || "—"} → {a.end_date || "—"}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-700">{a.short_text}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`ann-edit-${a.id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => askDelete(a)} className="text-red-600 hover:bg-red-50" data-testid={`ann-del-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* Modal Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); else setDialogOpen(true); }}>
        <DialogContent className="max-w-2xl" data-testid="ann-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar aviso" : "Nuevo aviso"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            <div className="col-span-2"><Label>Título *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} data-testid="ann-title" /></div>
            <div className="col-span-2"><Label>Texto corto *</Label><Input value={form.short_text} onChange={e => set("short_text", e.target.value)} data-testid="ann-short" /></div>
            <div className="col-span-2"><Label>Texto largo (opcional)</Label><Textarea rows={3} value={form.long_text} onChange={e => set("long_text", e.target.value)} data-testid="ann-long" /></div>
            <div><Label>Centro</Label>
              <Select value={form.center} onValueChange={v => set("center", v)}>
                <SelectTrigger data-testid="ann-center"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CENTER_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Posición</Label>
              <Select value={form.position} onValueChange={v => set("position", v)}>
                <SelectTrigger data-testid="ann-position"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(POSITION_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Variante visual</Label>
              <Select value={form.variant} onValueChange={v => set("variant", v)}>
                <SelectTrigger data-testid="ann-variant"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(VARIANT_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Orden</Label><Input type="number" value={form.order} onChange={e => set("order", parseInt(e.target.value)||100)} data-testid="ann-order" /></div>
            <div><Label>Fecha inicio</Label><Input type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} data-testid="ann-start" /></div>
            <div><Label>Fecha fin</Label><Input type="date" value={form.end_date} onChange={e => set("end_date", e.target.value)} data-testid="ann-end" /></div>
            <div><Label>Botón 1 texto</Label><Input value={form.button1_text} onChange={e => set("button1_text", e.target.value)} data-testid="ann-btn1-text" /></div>
            <div><Label>Botón 1 URL</Label><Input value={form.button1_url} onChange={e => set("button1_url", e.target.value)} placeholder="/cita o https://..." data-testid="ann-btn1-url" /></div>
            <div><Label>Botón 2 texto</Label><Input value={form.button2_text} onChange={e => set("button2_text", e.target.value)} data-testid="ann-btn2-text" /></div>
            <div><Label>Botón 2 URL</Label><Input value={form.button2_url} onChange={e => set("button2_url", e.target.value)} placeholder="https://wa.me/..." data-testid="ann-btn2-url" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => set("active", v)} data-testid="ann-active" /><Label>Activo</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.show_on_mobile} onCheckedChange={v => set("show_on_mobile", v)} data-testid="ann-mobile" /><Label>Mostrar en móvil</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.dismissible} onCheckedChange={v => set("dismissible", v)} data-testid="ann-dismissible" /><Label>Permite cerrar con ×</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="ann-cancel">Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title.trim() || !form.short_text.trim()} data-testid="ann-save">{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal confirmación eliminación */}
      <Dialog open={deleteTarget !== null} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirm(""); } }}>
        <DialogContent className="max-w-md" data-testid="ann-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Eliminar aviso</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Vas a eliminar el aviso:</p>
            <p className="font-mono bg-slate-100 p-2 rounded-sm">{deleteTarget?.title}</p>
            <p className="text-slate-600">Esta acción es <strong>irreversible</strong>. Para confirmar, escribe <strong>ELIMINAR</strong>:</p>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="ELIMINAR"
              data-testid="ann-delete-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirm(""); }} data-testid="ann-delete-cancel">Cancelar</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteConfirm.trim().toUpperCase() !== "ELIMINAR"}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="ann-delete-confirm"
            >
              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
